/**
 * Given a Game instance, it will map the game properties to a serializable state
 * @param  {Game} game - an instance of a game
 */
class GameState {
	constructor(game) {
		this._game = game;
	}

	/**
   * The current game state, suitable for serializing and for use with DealParser.fromPBNObject
   *
   * @example
   * {
        name: 'Test New Game State',
        auction: {
          value: 'S',
          bids: ['3S', 'P', 'P', 'P'],
        },
        contract: '3S',
        deal:
          'S:26TA.37J.48Q.59K 37J.48Q.59K.26TA 48Q.59K.26TA.37J 59K.26TA.37J.48Q',
        actions: ['2C', '3C', '4C'],
        vulnerable: 'EW
      }
   *
   * @return {GameState} the current game state
   */
	get state() {
		// Deal
		// Actions
		// Auction
		//  {
		//    deal: "W:KQT.AT9.J763.Q54 96532.765.542.K7 7.KQJ432.Q8.T982 AJ84.8.AKT9.AJ63",
		//    auction: {
		//      value: "W",
		//      bids: ["P", "3NT", "P", "P", "P"]
		//    },
		//    contract: "3NT"
		//    name: "Weak 2s "
		//    actions: []
		//  }
		//

		let state = {
			name: this._game.name,
			board: this._game.board || null,
			deal: this._game.dealString,
			auction: {
				value: this._game.dealer.name,
				bids: this._game.auction,
			},
			contract: this._game.contract,
			actions: this._game.cardsPlayed.map((card) => card.name),
			vulnerable: this._game.vulnerable,
			useDDS: this._game.useDDS,

			// to match PBN play tag format
			// play: null,
		};

		// TODO: decide if play tag is needed
		// if (this._game.declarer != null) {
		//   state.play = {
		//     value: this._game.declarer.seat.left,
		//     cards: this._game.cardsPlayed.map(
		//       (card) => `${card.suit.short}${card.rank.rank}`
		//     ),
		//   };
		// }

		return state;
	}

	/**
	 * Given a played card (string), return the state before it was played
	 * @param  {String} action - the name of a card played - e.g. `2C`
	 * @return {Object} an Object representing the state of the game
	 */
	stateBeforeAction(action) {
		return this.stateAtAction(action, 0);
	}

	/**
	 * Given a played card (string), return the state before it was played
	 * @param  {String} action - the name of a card played - e.g. `2C`
	 * @return {Object} an Object representing the state of the game
	 */
	stateAtAction(action, offset = 1) {
		let newState = Object.assign({}, this.state);
		newState.actions = newState.actions.slice(
			0,
			newState.actions.indexOf(action) + offset
		);
		return newState;
	}

	/**
	 * Return the state before the last card was played
	 * @return {Object} an Object representing the state of the game
	 */
	get stateAtLastCard() {
		let newState = Object.assign({}, this.state);
		newState.actions = newState.actions.slice(0, -1);
		return newState;
	}

	/**
	 * State of game after the auction is complete, before cards are played
	 * @return {Object} an Object representing the state of the game
	 */
	get stateAtEndOfAuction() {
		let newState = Object.assign({}, this.state);
		newState.actions = [];
		// newState.play.cards = [];
		return newState;
	}

	/**
	 * State before any bids have been made
	 * @return {Object} an Object representing the state of the game
	 */
	get stateBeforeAuction() {
		let newState = Object.assign({}, this.state);
		newState.auction.bids = [];
		newState.actions = [];
		// newState.play = null;
		return newState;
	}

	/**
	 * State before the last bid was made, resets any actions (cards played)
	 * @return {Object} an object representing the state of the game
	 */
	get stateAtLastBid() {
		let newState = Object.assign({}, this.state);
		newState.actions = [];
		newState.auction.bids = newState.auction.bids.slice(0, -1);

		// NOTE: contract here is purely used for information, so when the game state is parsed into a game, the contract is set based on the bids.
		// however, this could be misleading if the result of this call is stored somewhere directly, so set contract to undefined
		// newState.contract = null;
		newState.contract = undefined;
		return newState;
	}

	/**
	 * The state before cards have been dealt
	 * @return {Object}
	 */
	get stateBeforeDeal() {
		// TODO: undo deal cards?
		const newState = this.stateBeforeAuction;
		newState.deal = `${newState.auction.value}:- - - -`;
		return newState;
	}

	/**
	 * Go back one step and return the state object, either un-play a card, or un-bid a bid, or un-deal cards
	 *
	 * @return {Object}
	 */
	undo() {
		// if no actions, then attempt to undo bids
		if (this.state.actions.length > 0) {
			return this.stateAtLastCard;
		} else if (this.state.auction.bids.length > 0) {
			return this.stateAtLastBid;
		} else {
			return this.stateBeforeDeal;
		}
	}

	/**
	 * get the state of the game before the current trick
	 * @return {Object} an Object representing the state of the game
	 */
	get lastTrick() {
		if (this._game.tricks.length == 0) {
			return this.state; // no tricks to undo
		}
		// this.game.currentTrick.cards;
		if (this._game.currentTrick.cards.length > 0) {
			return this.stateBeforeAction(this._game.currentTrick.cards[0].name);
		}
		return this.stateBeforeAction(
			this._game.tricks[this._game.tricks.length - 2].cards[0].name
		);
	}
}

module.exports = GameState;
